/**
 * Admin-panel access, client side.
 *
 * This mirrors the server's section map so the UI doesn't offer what would be
 * refused. It is a convenience, NOT a control: every admin endpoint re-checks
 * the caller's sections and outlet, so a manager who edits the bundle or types
 * a URL still gets a 403 (or an empty, outlet-filtered result).
 */

/** Which section governs each admin route. Longest paths first. */
const SECTION_BY_ROUTE = [
  ['/admin/app', 'app'],
  ['/admin/notifications', 'notifications'],
  ['/admin/products', 'catalogue'],
  ['/admin/deliveries', 'deliveries'],
  ['/admin/customers', 'customers'],
  ['/admin/reviews', 'customers'],
  ['/admin/coupons', 'coupons'],
  ['/admin/stock', 'stock'],
  ['/admin/expenses', 'expenses'],
  ['/admin/payouts', 'expenses'],
  ['/admin/reports', 'reports'],
  ['/admin/announcements', 'announcements'],
  ['/admin/settings', 'settings'],
  ['/admin', 'dashboard'],
];

export function sectionForRoute(pathname) {
  const hit = SECTION_BY_ROUTE.find(([prefix]) => pathname === prefix || pathname.startsWith(`${prefix}/`));
  return hit ? hit[1] : null;
}

/** The app admin: a full admin plus the technical App admin section. */
export const isAppAdmin = (user) => user?.role === 'app_admin';

export const isFullAdmin = (user) => user?.role === 'admin' || isAppAdmin(user);

/** An outlet manager with at least one granted section. */
export const isOutletManager = (user) =>
  user?.role === 'outlet_manager' && (user?.adminSections?.length ?? 0) > 0;

/** May this user reach the admin area at all? */
export const canUseAdminArea = (user) => isFullAdmin(user) || isOutletManager(user);

/** May this user open a given section? Admins hold everything. */
export function hasSection(user, section) {
  // The app admin's own: API keys and providers, and the business settings
  // that decide fees, slots and what rings. An ordinary admin runs the shop;
  // changing the rules it runs by is a separate job.
  if (section === 'app' || section === 'settings') return isAppAdmin(user);
  if (isFullAdmin(user)) return true;
  // Everyone in the admin area gets their own inbox.
  if (section === 'notifications') return canUseAdminArea(user);
  if (!section) return false;
  return (user?.adminSections ?? []).includes(section);
}

/** The first section this user can actually open, for redirecting them home. */
export function firstAllowedRoute(user) {
  if (isFullAdmin(user)) return '/admin';
  const granted = user?.adminSections ?? [];
  const hit = SECTION_BY_ROUTE.find(([, section]) => granted.includes(section));
  return hit ? hit[0] : null;
}
